import { useActiveSessionStore } from '@ctct/rise-app-context';
import { env } from '@ctct/rise-env';
import { context } from '@opentelemetry/api';
import { type Logger, logs } from '@opentelemetry/api-logs';
import pickBy from 'lodash/pickBy';
import { type Level, type LogEvent } from 'pino';

import { getSpanId, getTraceId } from './traceUtils';
import { ACCOUNT_ID_KEY, PARTNER_ID_KEY, USER_ID_KEY } from './userContextPropagator';

let logger: Logger;

declare global {
  interface Window {
    PageState: {
      accountId: string;
      userId: string;
      partnerId: string;
    };
  }
}

const sendToOtel = async (level: Level, logEvent: LogEvent): Promise<void> => {
  if (process.env.NODE_ENV !== 'production') return;
  // Set in `instrumentation-client.ts` using `logs.setGlobalLoggerProvider`
  if (!logger) logger = logs.getLogger('otel');

  const time = new Date(logEvent.ts);
  const contextAttributes = getLogAttributes();
  const bindings = logEvent.bindings.reduce<Record<string, string>>((a, b) => ({ ...a, ...b }), {});

  // All structured fields are set as OTLP log record attributes so the
  // observability pipeline can query them without parsing the body.
  const logAttributes = {
    level: level.toUpperCase(),
    ...contextAttributes,
    ...bindings,
  };

  const message = logEvent.messages
    .map((m) => (typeof m === 'object' ? JSON.stringify(m) : m))
    .join('');

  logger.emit({
    timestamp: time,
    severityText: logEvent.level.label,
    severityNumber: logEvent.level.value,
    body: JSON.stringify({ time: time.toISOString(), message, ...logAttributes }),
    attributes: logAttributes,
  });
};

const getAccountDataFromOtelContext = () => {
  const activeContext = context.active();

  const accountId = activeContext.getValue(ACCOUNT_ID_KEY);
  const userId = activeContext.getValue(USER_ID_KEY);
  const partnerId = activeContext.getValue(PARTNER_ID_KEY);

  return pickBy(
    {
      accountId: typeof accountId === 'string' ? accountId : undefined,
      userId: typeof userId === 'string' ? userId : undefined,
      partnerId: typeof partnerId === 'string' ? partnerId : undefined,
    },
    (v) => v !== undefined
  );
};

const getAccountDataFromSession = () => {
  const { accountId, partnerId, userId, planGroupSubType } = useActiveSessionStore.getState();
  if (!accountId) return getAccountDataFallback();

  return {
    accountId,
    partnerId,
    userId,
    planGroupSubType,
  };
};

const getLogAttributes = () => {
  const accountData =
    typeof window === 'undefined' ? getAccountDataFromOtelContext() : getAccountDataFromSession();
  const spanId = getSpanId();
  const traceId = getTraceId();
  const gitCommit = env('NEXT_PUBLIC_GIT_COMMIT');

  return pickBy({ ...accountData, spanId, traceId, gitCommit }, (v) => v !== undefined);
};

const getAccountDataFallback = () => {
  if (typeof window === 'undefined') return {};
  // In some cases (like a fatal error in a layout that triggers the global error page)
  // we might not have the store available - try and get the info from PageState or
  // the distui_account_data cookie instead so we can still identify affected users
  try {
    return getFromPageState() ?? getFromCookie() ?? {};
  } catch {
    return {};
  }
};

const getFromPageState = () => {
  if (window.PageState.accountId) {
    return {
      accountId: window.PageState.accountId,
      userId: window.PageState.userId,
      partnerId: window.PageState.partnerId,
    };
  }
  return null;
};

const getFromCookie = () => {
  const cookie = document.cookie
    .split(';')
    .map((c) => c.trim().split('='))
    .find((c) => c[0] === 'distui_account_data');
  const data = cookie ? cookie[1] : undefined;
  if (data) {
    // For some reason the cookie is double encoded
    // No partner ID in this cookie
    return JSON.parse(JSON.parse(data));
  }
  return null;
};

export { getLogAttributes, sendToOtel };
